import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AdjustmentsHorizontalIcon, CalendarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface ForecastConfigurationDemoProps extends TeaserDemoProps {
  config?: {
    timeHorizon: number;
    periodicity: string;
    selectedAccounts: string[];
    selectedStrategies: string[];
    initialCapital: number;
  };
}

const ForecastConfigurationDemoComponent: React.FC<ForecastConfigurationDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  config = {
    timeHorizon: 2,
    periodicity: 'daily',
    selectedAccounts: ['Elite Trading', 'Growth Portfolio'],
    selectedStrategies: ['Elite Scalper', 'Grid Master'],
    initialCapital: 50000
  }
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);
  const [animatedValues, setAnimatedValues] = useState({
    timeHorizon: 1,
    capital: 25000
  });

  // Simulate interactive controls
  React.useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedValues(prev => ({
        timeHorizon: prev.timeHorizon >= 3 ? 1 : prev.timeHorizon + 0.5,
        capital: prev.capital >= 75000 ? 25000 : prev.capital + 5000
      }));
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            rotate: [0, 90, 180, 270, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 4, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <AdjustmentsHorizontalIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Forecast Configuration</h4>
          <p className="text-gray-300 text-xs">Interactive parameters</p>
        </div>
      </div>

      {/* Configuration Controls */}
      <div className="space-y-4 flex-1">
        {/* Time Horizon Slider */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300 text-sm flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" />
              Time Horizon
            </span>
            <motion.span
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
              className="text-white font-bold"
            >
              {animatedValues.timeHorizon.toFixed(1)} years
            </motion.span>
          </div>
          <div className="relative">
            <div className="w-full bg-black/30 rounded-full h-2">
              <motion.div
                animate={{ width: `${(animatedValues.timeHorizon / 3) * 100}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-gradient-to-r from-indigo-400 to-cyan-400 rounded-full"
              />
            </div>
            <motion.div
              animate={{ left: `${(animatedValues.timeHorizon / 3) * 100}%` }}
              transition={{ duration: 0.3 }}
              className="absolute -top-1 w-4 h-4 bg-cyan-400 rounded-full shadow-lg transform -translate-x-1/2"
            />
          </div>
        </motion.div>

        {/* Capital Amount */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-300 text-sm flex items-center gap-1">
              <CurrencyDollarIcon className="w-4 h-4" />
              Initial Capital
            </span>
            <motion.span
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="text-white font-bold"
            >
              ${animatedValues.capital.toLocaleString()}
            </motion.span>
          </div>
          <div className="relative">
            <div className="w-full bg-black/30 rounded-full h-2">
              <motion.div
                animate={{ width: `${((animatedValues.capital - 25000) / 50000) * 100}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
              />
            </div>
          </div>
        </motion.div>

        {/* Account Selection */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3"
        >
          <p className="text-gray-300 text-sm mb-2">Selected Accounts</p>
          <div className="flex flex-wrap gap-2">
            {config.selectedAccounts.map((account, index) => (
              <motion.span
                key={account}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 + index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
                className="px-2 py-1 bg-blue-500/30 text-blue-300 rounded-full text-xs border border-blue-500/40"
              >
                {account}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Strategy Selection */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-3"
        >
          <p className="text-gray-300 text-sm mb-2">Selected Strategies</p>
          <div className="flex flex-wrap gap-2">
            {config.selectedStrategies.map((strategy, index) => (
              <motion.span
                key={strategy}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.9 + index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
                className="px-2 py-1 bg-purple-500/30 text-purple-300 rounded-full text-xs border border-purple-500/40"
              >
                {strategy}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Generate Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.5 }}
        className="mt-4"
      >
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          animate={{ 
            boxShadow: [
              '0 0 10px rgba(59, 130, 246, 0.3)',
              '0 0 20px rgba(59, 130, 246, 0.5)',
              '0 0 10px rgba(59, 130, 246, 0.3)'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 px-4 rounded-lg font-medium text-sm shadow-lg"
        >
          Generate Forecast
        </motion.button>
      </motion.div>

      {/* Configuration Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="mt-2 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-cyan-300 text-xs font-medium"
        >
          🎛️ Dynamic parameter tuning
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const ForecastConfigurationDemo = createOptimizedTeaserDemo(
  ForecastConfigurationDemoComponent,
  'ForecastConfigurationDemo'
);